import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, getBaseUrl } from '../../utils/seo';

const RecruitPage = () => {
  // SEO設定
  useSEO({
    title: '採用情報・求人募集｜おかやま解体工事株式会社 - 岡山市の解体工事会社',
    description: '岡山市のおかやま解体工事株式会社の採用情報。解体工事スタッフ、造園スタッフ、営業・現場管理を募集中。未経験者歓迎、社会保険完備、資格取得支援制度あり。一緒に岡山の未来を創る仲間を募集しています。',
    keywords: '採用情報,求人,岡山市,解体工事,造園工事,未経験歓迎,正社員募集,建設業',
    canonicalPath: '/recruit',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: '解体工事・造園スタッフ募集',
      description: '岡山市で建物解体工事、造園工事を行う当社で一緒に働く仲間を募集しています。未経験者歓迎、充実した研修制度と資格取得支援制度があります。',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'おかやま解体工事株式会社',
        sameAs: getBaseUrl(),
        logo: `${getBaseUrl()}/logo.png`
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '北区○○町1-2-3',
          addressLocality: '岡山市',
          addressRegion: '岡山県',
          addressCountry: 'JP'
        }
      },
      employmentType: 'FULL_TIME',
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'JPY',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 230000,
          maxValue: 450000,
          unitText: 'MONTH'
        }
      },
      datePosted: '2025-01-15'
    }
  });

  const jobs = [
    {
      title: '解体工事スタッフ',
      type: '正社員',
      description: '建物解体工事の現場作業を担当していただきます。未経験の方も丁寧に指導しますので、安心してご応募ください。',
      duties: [
        '建物解体工事の現場作業',
        '重機の操作（資格取得支援あり）',
        '廃材の分別・搬出作業',
        '現場の安全管理',
      ],
      requirements: [
        '普通自動車免許（AT限定可）',
        '未経験者歓迎',
        '重機免許保有者優遇',
      ],
      conditions: '8:00〜17:00（休憩1時間）/ 日曜日・祝日',
      salary: '月給25万円〜40万円（経験・能力による）',
      image: 'https://public.readdy.ai/ai/img_res/abb2941e12eaee1265a2bade5494253d.jpg',
    },
    {
      title: '造園スタッフ',
      type: '正社員',
      description: '庭園の設計・施工・メンテナンスを担当していただきます。植物が好きな方、ものづくりが好きな方を歓迎します。',
      duties: [
        '庭園の設計・施工',
        '植栽・剪定作業',
        '石組み・水回りの設置',
        '定期メンテナンス',
      ],
      requirements: [
        '普通自動車免許',
        '未経験者歓迎',
        '造園技能士資格保有者優遇',
      ],
      conditions: '8:00〜17:00（休憩1時間）/ 日曜日・祝日',
      salary: '月給23万円〜38万円（経験・能力による）',
      image: 'https://readdy.ai/api/search-image?query=young%20Japanese%20male%20landscaping%20professional%20working%20on%20garden%20project%2C%20wearing%20work%20uniform%20and%20gloves%2C%20friendly%20smile%2C%20outdoor%20setting%20in%20Okayama%20with%20clean%20simple%20background&width=400&height=400&seq=recruit2&orientation=squarish',
    },
    {
      title: '営業・現場管理',
      type: '正社員',
      description: 'お客様対応から現場管理まで幅広く担当していただきます。コミュニケーション能力を活かせるお仕事です。',
      duties: [
        '新規顧客の開拓・既存顧客のフォロー',
        '見積もり作成・契約業務',
        '現場の進行管理',
        'スタッフの労務管理',
      ],
      requirements: [
        '普通自動車免許',
        '営業経験者優遇',
        'PCスキル（Word、Excel）',
      ],
      conditions: '8:00〜17:00（休憩1時間）/ 日曜日・祝日',
      salary: '月給28万円〜45万円（経験・能力による）',
      image: 'https://readdy.ai/api/search-image?query=professional%20Japanese%20male%20business%20person%20in%20work%20uniform%20with%20clipboard%20at%20construction%20site%2C%20confident%20and%20friendly%20demeanor%2C%20Okayama%20location%20with%20clean%20simple%20background&width=400&height=400&seq=recruit3&orientation=squarish',
    },
  ];

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: '社会保険完備',
      description: '健康保険、厚生年金、雇用保険、労災保険完備',
    },
    {
      icon: 'ri-book-open-line',
      title: '資格取得支援',
      description: '重機免許、造園技能士など資格取得費用を全額補助',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '昇給・賞与',
      description: '年1回昇給、年2回賞与（業績による）',
    },
    {
      icon: 'ri-calendar-check-line',
      title: '有給休暇',
      description: '入社6ヶ月後から有給休暇付与',
    },
    {
      icon: 'ri-gift-line',
      title: '退職金制度',
      description: '勤続3年以上で退職金制度適用',
    },
    {
      icon: 'ri-car-line',
      title: '社用車貸与',
      description: '通勤・業務用の社用車を貸与',
    },
  ];

  const steps = [
    { number: '01', title: '応募', description: 'フォームまたはお電話でご応募ください' },
    { number: '02', title: '書類選考', description: '履歴書・職務経歴書を確認させていただきます' },
    { number: '03', title: '面接', description: '代表または担当者との面接を実施します' },
    { number: '04', title: '職場見学', description: '実際の現場や職場を見学していただきます' },
    { number: '05', title: '内定・入社', description: '条件面談後、入社日を決定します' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/feb0defe1bd70692400bf85166d52c26.jpeg"
            alt="採用情報"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-4 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
            <i className="ri-team-line text-white w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white">未経験者歓迎</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            採用情報
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
            共に成長する仲間を募集しています
          </p>
        </div>
      </section>

      {/* Message Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
                代表メッセージ
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-8 leading-tight">
                一緒に岡山の未来を<br />創りませんか
              </h2>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed mb-8">
                <p>
                  おかやま解体工事株式会社は、創業30年以上の歴史を持つ地域密着型の企業です。私たちは、単なる解体工事や造園工事を行うだけでなく、お客様の新しい未来を創るお手伝いをしています。
                </p>
                <p>
                  当社では、未経験の方でも安心して働けるよう、丁寧な研修制度を整えています。資格取得支援制度も充実しており、あなたの成長を全力でサポートします。
                </p>
                <p>
                  若い力、新しい発想を持った方々と一緒に、岡山の街づくりに貢献していきたいと考えています。ぜひ、私たちと一緒に働きませんか。
                </p>
              </div>
              <p className="text-right text-primary font-medium text-xl italic">
                代表取締役 岡山太郎
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden shadow-xl h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20Japanese%20male%20company%20president%20in%20his%2050s%20wearing%20business%20casual%20attire%2C%20confident%20and%20warm%20smile%2C%20office%20or%20construction%20site%20background%20in%20Okayama%2C%20clean%20simple%20background%2C%20leadership%20presence&width=800&height=1000&seq=president&orientation=portrait"
                  alt="代表取締役 岡山太郎"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">募集職種</h2>
          <div className="max-w-5xl mx-auto space-y-8">
            {jobs.map((job, index) => (
              <article key={index} className="bg-white rounded-xl shadow-md p-8 lg:p-10 hover:shadow-xl transition-shadow">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                  <div className="lg:col-span-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                      <h3 className="text-primary font-bold text-2xl lg:text-3xl">{job.title}</h3>
                      <span className="inline-block bg-secondary text-white px-4 py-1 rounded-full text-sm font-medium w-fit">
                        {job.type}
                      </span>
                    </div>
                    <p className="text-gray-700 text-lg mb-6 leading-relaxed">{job.description}</p>
                    
                    <div className="space-y-4 mb-6">
                      <div>
                        <h4 className="text-primary font-bold mb-2">仕事内容</h4>
                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                          {job.duties.map((duty, idx) => (
                            <li key={idx}>{duty}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-primary font-bold mb-2">必要資格</h4>
                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                          {job.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-primary font-bold mb-2">勤務時間・休日</h4>
                        <p className="text-gray-700">{job.conditions}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-primary font-bold mb-2 text-xl">給与</h4>
                        <p className="text-primary font-bold text-xl">{job.salary}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:col-span-1 flex items-start justify-center">
                    <div className="w-48 h-48 lg:w-full lg:h-64 rounded-xl overflow-hidden shadow-md">
                      <img
                        src={job.image}
                        alt={job.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">
            福利厚生・待遇
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-7 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-primary font-bold text-xl mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Flow */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary text-center mb-12">
            応募の流れ
          </h2>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-4">
              {steps.map((step, index) => (
                <div key={index} className="flex-1 text-center">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-primary font-bold text-xl mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full">
                      <i className="ri-arrow-right-line text-gray-400 text-2xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="application" className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20beautiful%20Okayama%20cityscape%20with%20construction%20site%20in%20foreground%2C%20professional%20Japanese%20workers%20visible%20in%20distance%2C%20bright%20sunny%20day%20with%20clear%20sky%2C%20clean%20organized%20work%20environment%2C%20inspiring%20and%20professional%20atmosphere&width=1920&height=1080&seq=company-cta&orientation=landscape"
            alt="一緒に働きませんか"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              まずはお気軽に<br />ご応募ください
            </h2>
          </div>
          <div className="absolute bottom-12 right-8 text-right">
            <p className="text-white text-xl lg:text-2xl font-light mb-4">
              見学も随時受付中
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary rounded-full hover:bg-gray-100 transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </Link>
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center px-8 py-4 bg-secondary text-white rounded-full hover:bg-primary transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                086-012-3456
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RecruitPage;
